import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

export default function AuditLogs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/audit-logs", { params: { limit: 300 } }).then((r) => { setRows(r.data); setLoading(false); });
  }, []);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.user_name, r.user_email, r.action, r.entity_type, r.entity_id]
        .some((v) => (v || "").toString().toLowerCase().includes(needle))
    );
  }, [rows, q]);

  if (loading) return <div className="p-6"><Skeleton className="h-96 rounded-3xl" /></div>;
  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Audit logs.</h1>
      </header>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by user, action or entity…"
          className="pl-10 h-11 rounded-2xl bg-muted/40"
          data-testid="audit-search-input"
        />
      </div>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-3 space-y-1.5">
          {filteredRows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{q.trim() ? "No entries match your search." : "No activity yet."}</p>
          ) : filteredRows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border/60 hover:bg-muted/30 transition p-3 flex flex-col gap-1.5 text-xs lg:grid lg:grid-cols-12 lg:gap-3 lg:items-center lg:flex-row" data-testid={`audit-row-${r.id}`}>
              <div className="lg:col-span-3 min-w-0">
                <p className="font-semibold text-foreground truncate">{r.user_name || "—"}</p>
                <p className="text-muted-foreground truncate">{r.user_email}</p>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap lg:contents">
                <Badge variant="outline" className="font-mono text-[10px] lg:col-span-3 lg:justify-self-start whitespace-nowrap">{r.action}</Badge>
                <span className="text-muted-foreground truncate min-w-0 lg:col-span-3">{r.entity_type} · {r.entity_id?.slice(0, 8) || "—"}</span>
                <span className="text-muted-foreground whitespace-nowrap lg:col-span-3 lg:text-right">{fmtDateTime(r.timestamp)}</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
